import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { SalesAccount, SalesAccounts } from "../settingsApi";
import {
  GridColDef,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { ToggleBooleanCell } from "../helpers/ToggleBooleanCell";
import {
  fetchSalesAccountConfigDates,
  fetchSalesAccountConfigForDate,
  updateSalesAccounts,
} from "../settingsActions";
import { useAppDispatch } from "../../../app/hooks";
import { isFulfilled } from "@reduxjs/toolkit";
import { handleSalesAccountsData } from "../helpers/utils";
import { CreateSalesAccountsConfig } from "./CreateSalesAccountsConfig";
import { EditSalesAccountsConfig } from "./EditSalesAccountsConfig";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";

export const SalesAccountsControlPanel = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("settings");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewSet, setIsNewSet] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [salesAccountConfigDates, setSalesAccountConfigDates] = useState<
    string[]
  >([]);
  const [selectedDate, setSelectedDate] = useState<string | null>();
  const [salesAccountsData, setSalesAccountsData] = useState<SalesAccounts>({
    accounts: [],
    validFrom: "",
    version: 0,
  });
  const [salesAccountRows, setSalesAccountRows] = useState<SalesAccount[]>([]);

  const validateVatCode = (vatCode: string) => {
    const vatCodePattern = /AE|^[SEGK]\b/;
    const vatCodeNotValid = !vatCode.match(vatCodePattern);
    return vatCodeNotValid ? t("validationVatCode") : null;
  };

  const validateCode = (code: string) => {
    const lengthNotValid = code.length < 2 || code.length > 8;
    const accountCodePattern = /^[\w-_]+$/;
    const patternNotValid = !code.match(accountCodePattern);
    if (lengthNotValid) {
      return t("validationAccountCodeLength");
    } else if (patternNotValid) {
      return t("codeCannotContainSpecialCharacters");
    }
    return null;
  };

  const validateName = (name: string) => {
    const isNotValid = name.length < 1 || name.length > 100;
    return isNotValid ? t("validationAccountNameLength") : null;
  };

  const preProcessVatCodeCellProps = (params: GridPreProcessEditCellProps) => {
    const errorMessage = validateVatCode(params.props.value!.toString());
    return { ...params.props, error: errorMessage };
  };

  const preProcessCodeCellProps = (params: GridPreProcessEditCellProps) => {
    const errorMessage = validateCode(params.props.value!.toString());
    return { ...params.props, error: errorMessage };
  };

  const preProcessNameCellProps = (params: GridPreProcessEditCellProps) => {
    const errorMessage = validateName(params.props.value!.toString());
    return { ...params.props, error: errorMessage };
  };

  // weird typing issue, only this one needs typing to work
  const salesAccountColumns: GridColDef<any, any, any>[] = [
    {
      field: "code",
      headerName: t("accountCode") || "",
      width: 180,
      editable: true,
      preProcessEditCellProps: preProcessCodeCellProps,
    },
    {
      field: "description",
      headerName: t("description") || "",
      width: 280,
      editable: true,
      preProcessEditCellProps: preProcessNameCellProps,
    },
    {
      field: "defaultVatPercent",
      headerName: t("VATpercent") || "",
      type: "number",
      width: 180,
      editable: true,
    },
    {
      field: "defaultVatCode",
      headerName: t("VATcode") || "",
      width: 180,
      editable: true,
      preProcessEditCellProps: preProcessVatCodeCellProps,
    },
    {
      field: "canUseInContract",
      headerName: t("useInContract") || "",
      type: "boolean",
      width: 180,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ToggleBooleanCell params={params} />
      ),
    },
    {
      field: "canUseInExternalSales",
      headerName: t("useInExternalSales") || "",
      width: 180,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ToggleBooleanCell params={params} />
      ),
    },
    {
      field: "canUseInGroupInternalSales",
      headerName: t("useInGroupInternalSales") || "",
      width: 180,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ToggleBooleanCell params={params} />
      ),
    },
  ];

  const getSalesAccountConfigDates = useCallback(() => {
    dispatch(fetchSalesAccountConfigDates(null)).then((action) => {
      isFulfilled(action) && setSalesAccountConfigDates(action.payload);
      setIsLoading(false);
    });
  }, [dispatch]);

  const getSalesAccountConfigsForDate = useCallback(() => {
    if (!selectedDate) return;
    dispatch(fetchSalesAccountConfigForDate({ validFrom: selectedDate })).then(
      (action) => {
        isFulfilled(action) && setSalesAccountsData(action.payload);
        setIsLoading(false);
      },
    );
  }, [dispatch, selectedDate]);

  const saveSalesAccounts = (modifiedAccounts: SalesAccount[]) => {
    setIsLoading(true);
    const array = modifiedAccounts
      .filter((item: any) => item.status !== "deleted")
      .reduce((map: any, obj: any) => {
        map[obj.code] = {
          ...obj,
          defaultVatPercent: obj.defaultVatPercent.toString(),
        };
        return map;
      }, {});
    const newAccounts: SalesAccounts = {
      version: salesAccountsData.version,
      validFrom: !isNewSet ? salesAccountsData.validFrom : selectedDate!!,
      accounts: array,
    };
    dispatch(updateSalesAccounts(newAccounts)).then((action) => {
      if (isFulfilled(action)) {
        setSalesAccountsData(action.payload[0]);
        closeNewConfiguration();
      }
    });
  };

  const setupNewConfiguration = (selectedDate: Date) => {
    setIsNewSet(true);
    const dateString =
      selectedDate.getFullYear() +
      "-" +
      ("0" + (selectedDate.getMonth() + 1)).slice(-2) +
      "-" +
      ("0" + selectedDate.getDate()).slice(-2);
    setSelectedDate(dateString);
    setIsModalOpen(false);
  };

  const closeNewConfiguration = () => {
    setIsNewSet(false);
    setIsModalOpen(false);
    setSelectedDate(null);
    setSalesAccountsData({ accounts: [], validFrom: "", version: 0 });
    setSalesAccountRows([]);
    getSalesAccountConfigDates();
  };

  useEffect(() => {
    setIsLoading(true);
    getSalesAccountConfigDates();
  }, [getSalesAccountConfigDates]);

  useEffect(() => {
    setIsLoading(true);
    getSalesAccountConfigsForDate();
  }, [selectedDate, getSalesAccountConfigsForDate]);

  useEffect(() => {
    if (salesAccountsData !== undefined) {
      const array = handleSalesAccountsData(salesAccountsData.accounts);
      setSalesAccountRows(array);
    }
  }, [salesAccountsData]);

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      {isNewSet ? (
        <CreateSalesAccountsConfig
          columns={salesAccountColumns}
          rows={salesAccountRows}
          onSave={saveSalesAccounts}
          onUndo={getSalesAccountConfigsForDate}
          onClose={closeNewConfiguration}
          selectedDate={selectedDate || ""}
        />
      ) : (
        <EditSalesAccountsConfig
          columns={salesAccountColumns}
          rows={salesAccountRows}
          onSave={saveSalesAccounts}
          onUndo={getSalesAccountConfigsForDate}
          selectedDate={selectedDate || ""}
          cancelNew={closeNewConfiguration}
          setupNew={setupNewConfiguration}
          salesAccountConfigDates={salesAccountConfigDates}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          setSelectedDate={setSelectedDate}
        />
      )}
    </>
  );
};
